/**
 * Monthly Checklist Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  MonthlyChecklistDTO,
  ChecklistItemDTO,
  ProgressStatsDTO,
} from "../models/MonthlyChecklistDTOs";
import {
  MonthlyChecklistEntity,
  ChecklistItemEntity,
  ProgressStatsEntity,
} from "../../domain/entities/MonthlyChecklistEntity";

export class MonthlyChecklistMapper {
  /**
   * Convert MonthlyChecklistDTO to MonthlyChecklistEntity
   */
  static toChecklistEntity(dto: MonthlyChecklistDTO): MonthlyChecklistEntity {
    return {
      id: dto.id,
      template_id: dto.template_id,
      template_name: dto.template_name,
      month_year: dto.month_year,
      status: dto.status,
      closed_by: dto.closed_by,
      closed_at: dto.closed_at,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      progress: dto.progress ? this.toProgressStatsEntity(dto.progress) : undefined,
    };
  }

  /**
   * Convert ProgressStatsDTO to ProgressStatsEntity
   */
  static toProgressStatsEntity(dto: ProgressStatsDTO): ProgressStatsEntity {
    return {
      total: dto.total,
      completed: dto.completed,
      in_progress: dto.in_progress,
      not_started: dto.not_started,
      pending_review: dto.pending_review,
    };
  }

  /**
   * Convert ChecklistItemDTO to ChecklistItemEntity
   */
  static toItemEntity(dto: ChecklistItemDTO): ChecklistItemEntity {
    // Parse JSON fields to arrays
    let taskGuidance: string[] = [];
    if (typeof dto.task_guidance === "string") {
      try {
        taskGuidance = JSON.parse(dto.task_guidance);
      } catch {
        taskGuidance = [];
      }
    } else if (Array.isArray(dto.task_guidance)) {
      taskGuidance = dto.task_guidance;
    }

    let acceptedProofTypes: string[] = ["screenshot"];
    if (typeof dto.accepted_proof_types === "string") {
      try {
        acceptedProofTypes = JSON.parse(dto.accepted_proof_types);
      } catch {
        acceptedProofTypes = ["screenshot"];
      }
    } else if (Array.isArray(dto.accepted_proof_types)) {
      acceptedProofTypes = dto.accepted_proof_types;
    }

    // Handle is_deleted: convert string "0"/"1" to boolean, or use boolean directly
    let isDeleted = false;
    if (dto.is_deleted === undefined || dto.is_deleted === null) {
      isDeleted = false;
    } else if (typeof dto.is_deleted === "boolean") {
      isDeleted = dto.is_deleted;
    } else if (typeof dto.is_deleted === "string") {
      // Explicitly handle string values: "1" or "true" = true, everything else (including "0", "false", "") = false
      const normalized = dto.is_deleted.toLowerCase().trim();
      isDeleted = normalized === "1" || normalized === "true";
    } else if (typeof dto.is_deleted === "number") {
      isDeleted = dto.is_deleted === 1;
    }

    return {
      id: dto.id,
      checklist_id: dto.checklist_id,
      template_item_id: dto.template_item_id,
      is_deleted: isDeleted,
      deleted_at: dto.deleted_at,
      accounting_area: dto.accounting_area,
      title: dto.title,
      description: dto.description,
      task_guidance: taskGuidance,
      accepted_proof_types: acceptedProofTypes,
      requires_proof: dto.requires_proof,
      order_index: dto.order_index,
      priority:
        dto.priority === "high" ||
        dto.priority === "medium" ||
        dto.priority === "low"
          ? dto.priority
          : undefined,
      link_url: dto.link_url,
      link_text: dto.link_text,
      system_info_query: dto.system_info_query,
      status: dto.status,
      notes: dto.notes,
      completed_by: dto.completed_by,
      completed_at: dto.completed_at,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  /**
   * Convert array of ChecklistItemDTO to ChecklistItemEntity array
   */
  static toItemEntityArray(items: ChecklistItemDTO[]): ChecklistItemEntity[] {
    return (items || []).map((dto) => this.toItemEntity(dto));
  }
}

